import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Wordmark } from '../lib/ui';
import { Icon } from '../lib/icons';
import { useSeo, SITE_ORIGIN } from '../lib/useSeo';
import GUIDE_ARTICLES from '../content/guideData';
import { GuideFooter } from './GuideArticle';

const CLUSTERS = [
  { key: 'A', label: 'พื้นฐานวงแชร์', desc: 'เริ่มจากศูนย์ — วงแชร์คืออะไร เล่นยังไง' },
  { key: 'D', label: 'เริ่มต้นตั้งวง', desc: 'ลงมือตั้งวงแชร์วงแรกแบบทีละขั้น' },
  { key: 'B', label: 'สำหรับท้าวแชร์', desc: 'ตั้งวง คิดดอก คุมวง ให้เป็นมืออาชีพ' },
  { key: 'C', label: 'กฎหมาย & ความน่าเชื่อถือ', desc: 'พ.ร.บ. 2534 เครดิตท้าว สัญญา ความเสี่ยง' },
  { key: 'ToolHub', label: 'ตรวจหลักฐาน & เมื่อโดนโกง', desc: 'ตรวจซองหลักฐานเอง แจ้งความ ฟ้องศาล' },
  { key: 'group-buy', label: 'แชร์ซื้อของ', desc: 'แชร์ทอง/ของ จับสลากยุติธรรมตรวจสอบได้' },
  { key: '__other', label: 'คู่มืออื่น ๆ', desc: '' },
];

export default function GuideIndex() {
  const nav = useNavigate();

  useSeo({
    title: 'คู่มือเล่นแชร์ปลอดภัย — ดอกหัก/ดอกตาม กฎหมาย โดนโกงทำไง | เปียแชร์',
    description: 'รวมคู่มือเล่นแชร์ฉบับเข้าใจง่าย: ดอกหัก-ดอกตาม วิธีตั้งวง พ.ร.บ.การเล่นแชร์ 2534 สัญญาณท้าวแชร์จะหนี โดนโกงแชร์ทำยังไง และวิธีตรวจหลักฐานเอง',
    path: '/guide',
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'CollectionPage',
        name: 'คู่มือเล่นแชร์ปลอดภัย', inLanguage: 'th-TH',
        url: `${SITE_ORIGIN}/guide`,
        description: 'รวมบทความคู่มือการเล่นแชร์ (ROSCA) สำหรับลูกแชร์และท้าวแชร์',
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'หน้าแรก', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: 'คู่มือเล่นแชร์', item: `${SITE_ORIGIN}/guide` },
        ],
      },
    ],
  });

  return (
    <div style={{ minHeight: '100vh', background: PS.page, color: PS.text, fontFamily: PS.fontUI }}>
      <div style={{ borderBottom: `1px solid ${PS.line}`, background: PS.surface }}>
        <div style={{ maxWidth: 880, margin: '0 auto', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button data-testid="guide-home-btn" onClick={() => nav('/')} className="ps-press" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><Wordmark size={20} /></button>
          <button data-testid="guide-open-app" onClick={() => nav('/app')} className="ps-press" style={{ border: 'none', background: PS.ink, color: PS.page, borderRadius: 999, padding: '8px 16px', fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI }}>เปิดแอป</button>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '40px 22px 24px' }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: PS.terracotta, letterSpacing: 0.5, textTransform: 'uppercase' }}>คู่มือเล่นแชร์ปลอดภัย</div>
        <h1 style={{ fontSize: 'clamp(28px,5vw,40px)', fontWeight: 700, letterSpacing: -0.8, lineHeight: 1.2, margin: '10px 0 14px' }}>เล่นแชร์ให้เข้าใจ ไม่เสียเปรียบ ไม่โดนโกง</h1>
        <p style={{ fontSize: 16, color: PS.textMuted, lineHeight: 1.7, maxWidth: 620, margin: 0 }}>รวมความรู้เรื่องการเล่นแชร์ทั้งหมด ตั้งแต่ดอกหัก-ดอกตาม วิธีตั้งวง กฎหมาย พ.ร.บ. 2534 ไปจนถึงเมื่อโดนโกงต้องทำยังไง — เขียนให้คนเล่นแชร์จริงอ่านเข้าใจ</p>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 22px 60px' }}>
        {CLUSTERS.map((cl) => {
          const known = ['A', 'D', 'B', 'C', 'ToolHub', 'group-buy'];
          const arts = cl.key === '__other'
            ? GUIDE_ARTICLES.filter((a) => !known.includes(a.cluster))
            : GUIDE_ARTICLES.filter((a) => a.cluster === cl.key);
          if (!arts.length) return null;
          return (
            <section key={cl.key} style={{ marginBottom: 38 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.3 }}>{cl.label}</div>
                <div style={{ fontSize: 13, color: PS.textMuted, marginTop: 3 }}>{cl.desc}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {arts.map((a) => (
                  <a key={a.slug} href={`/guide/${a.slug}`} data-testid={`guide-card-${a.slug}`}
                    onClick={(e) => { e.preventDefault(); nav(`/guide/${a.slug}`); }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 18, background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, textDecoration: 'none', color: PS.text }}>
                    {a.pageType === 'pillar' && <span style={{ alignSelf: 'flex-start', fontSize: 10, fontWeight: 700, color: PS.terracotta, background: `${PS.terracotta}1a`, padding: '2px 8px', borderRadius: 999 }}>บทความหลัก</span>}
                    <div style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.4 }}>{a.title}</div>
                    <div style={{ fontSize: 12.5, color: PS.textMuted, lineHeight: 1.55, flex: 1 }}>{a.metaDescription}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: PS.terracotta, marginTop: 2 }}>อ่านต่อ <Icon.chevronR size={14} stroke={PS.terracotta} /></div>
                  </a>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <GuideFooter nav={nav} />
    </div>
  );
}
